import { trackEvent } from '@/common/store/pinia/telemetry/core/client';
import { toSeconds } from '@/common/store/pinia/telemetry/core/utils';
import type { FramesPerSecondSample } from '@/common/store/pinia/telemetry/core/types';
import { throttle } from 'lodash-es';

const MIN_SAMPLE_DURATION_MS = 250;
const SCROLL_FPS_TRACKING_DURATION_MS = 1000;
const SCROLL_THROTTLE_MS = 200;

export const trackFramesPerSecond = (
  sample: FramesPerSecondSample,
  additionalProperties?: Record<string, unknown>
): void => {
  const fpsExact = Number(sample.fps.toFixed(2));
  const properties: Record<string, unknown> = {
    fps_avg: fpsExact,
    fps_rounded: Math.round(sample.fps),
    frame_count: sample.frameCount,
    sample_duration_ms: Math.round(sample.sampleDurationMs),
    sample_duration_seconds: toSeconds(sample.sampleDurationMs),
    sample_start_ms: Math.round(sample.sampleStartTime),
    sample_end_ms: Math.round(sample.sampleEndTime),
    low_fps: fpsExact < 55,
    ...additionalProperties
  };

  trackEvent('frames_per_second_sampled', properties);
};

interface FpsTrackingSession {
  rafId: number | null;
  startTime: number;
  frameCount: number;
  onComplete?: (fps: number, duration: number) => void;
  additionalProperties?: Record<string, unknown>;
}

const activeSessions = new Map<string, FpsTrackingSession>();

const emitFpsSample = (
  sessionId: string,
  endTime: number,
  additionalProperties?: Record<string, unknown>
): void => {
  const session = activeSessions.get(sessionId);
  if (!session) {
    return;
  }

  const duration = endTime - session.startTime;
  if (duration < MIN_SAMPLE_DURATION_MS || session.frameCount <= 0) {
    return;
  }

  const fps = session.frameCount / (duration / 1000);

  trackFramesPerSecond({
    fps,
    frameCount: session.frameCount,
    sampleDurationMs: duration,
    sampleStartTime: session.startTime,
    sampleEndTime: endTime
  }, additionalProperties);

  if (session.onComplete) {
    session.onComplete(fps, duration);
  }
};

const handleAnimationFrame = (sessionId: string, _timestamp: number): void => {
  const session = activeSessions.get(sessionId);
  if (!session) {
    return;
  }

  session.frameCount++;
  session.rafId = requestAnimationFrame((ts) => handleAnimationFrame(sessionId, ts));
};

export const stopFpsTracking = (sessionId: string): void => {
  const session = activeSessions.get(sessionId);
  if (!session) {
    return;
  }

  if (session.rafId !== null && typeof cancelAnimationFrame === 'function') {
    cancelAnimationFrame(session.rafId);
  }

  activeSessions.delete(sessionId);
};

export const trackFpsForDuration = (
  sessionId: string,
  durationMs: number,
  additionalProperties?: Record<string, unknown>,
  onComplete?: (fps: number, duration: number) => void
): void => {
  stopFpsTracking(sessionId);

  if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
    return;
  }

  const startTime = performance.now();
  const session: FpsTrackingSession = {
    rafId: null,
    startTime,
    frameCount: 0,
    onComplete,
    additionalProperties
  };

  activeSessions.set(sessionId, session);

  session.rafId = requestAnimationFrame((ts) => handleAnimationFrame(sessionId, ts));

  setTimeout(() => {
    const endTime = performance.now();
    emitFpsSample(sessionId, endTime, additionalProperties);
    stopFpsTracking(sessionId);
  }, durationMs);
};

export const cleanupFpsTracking = (): void => {
  activeSessions.forEach((_session, sessionId) => {
    stopFpsTracking(sessionId);
  });
};

let globalScrollListener: ((event: Event) => void) | null = null;
let isScrollMonitoringActive = false;

export const initializeScrollFpsTracking = (): void => {
  if (globalScrollListener) {
    return;
  }

  globalScrollListener = throttle(() => {
    if (isScrollMonitoringActive) {
      return;
    }

    isScrollMonitoringActive = true;
    const scrollSessionId = `window-scroll-${Date.now()}`;

    trackFpsForDuration(
      scrollSessionId,
      SCROLL_FPS_TRACKING_DURATION_MS,
      {
        event_type: 'scroll',
        context: 'window_scroll',
        element_type: 'window',
        app_section: 'global'
      },
      () => {
        isScrollMonitoringActive = false;
      }
    );
  }, SCROLL_THROTTLE_MS);

  window.addEventListener('scroll', globalScrollListener, { passive: true });
};

export const cleanupScrollFpsTracking = (): void => {
  if (globalScrollListener) {
    window.removeEventListener('scroll', globalScrollListener);
    globalScrollListener = null;
  }
  isScrollMonitoringActive = false;
};
